// src/pages/ceed/Dashboard.jsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  DollarSign,
  Bell,
  ArrowRight,
  Loader2,
  ShieldCheck,
  Users2,
  ClipboardList,
  Download,
  Mail,
  TrendingUp,
  RefreshCw,
  Activity,
} from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { can } from "../../config/roles";

function useIsMobile() {
  const [v, setV] = useState(() => window.innerWidth <= 767);
  useEffect(() => {
    const h = () => setV(window.innerWidth <= 767);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return v;
}

const BASE =
  process.env.REACT_APP_BACKEND_URL || import.meta.env?.VITE_API_BASE_URL || "";

async function jfetch(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    cache: "no-store",
    ...opts,
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

/* ── Style injection ──────────────────────────────────────────── */
const STYLE_ID = "ceed-dashboard-styles-v2";
function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .ceed-db { font-family: 'DM Sans', sans-serif; color: #0c1118; }

    /* Page header */
    .ceed-db-header { margin-bottom: 28px; }
    .ceed-db-title {
      font-family: 'DM Sans', sans-serif;
      font-size: 24px; font-weight: 700; color: #0c1118; line-height: 1.2;
      margin-bottom: 4px;
    }
    .ceed-db-sub {
      font-family: 'DM Sans', sans-serif;
      font-size: 13px; color: #6b7280;
    }
    .ceed-db-refresh {
      display: flex; align-items: center; gap: 6px;
      padding: 7px 14px; border-radius: 7px;
      background: #0c1118; color: #fff;
      font-size: 13px; font-weight: 500;
      border: none; cursor: pointer;
      transition: background 160ms;
    }
    .ceed-db-refresh:hover { background: #1e2832; }
    .ceed-db-refresh:disabled { opacity: .5; cursor: default; }

    /* KPI grid */
    .ceed-kpi-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 14px;
      margin-bottom: 28px;
    }
    @media (max-width: 1023px) { .ceed-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 767px) {
      .ceed-kpi-grid { grid-template-columns: repeat(2, 1fr) !important; }
      .ceed-kpi-card { padding: 12px 14px; }
      .ceed-kpi-icon { display: none; }
      .ceed-kpi-value { font-size: 1.6rem; }
      .ceed-kpi-label { font-size: 9px; }
      .ceed-kpi-sub { display: none; }
      .ceed-list-row-sub { display: none; }
      .ceed-db-refresh span { display: none; }
    }

    .ceed-kpi-card {
      background: #fff;
      border: 1px solid #e8ecf0;
      border-radius: 10px;
      padding: 18px 20px;
      position: relative;
      overflow: hidden;
      transition: box-shadow 200ms;
    }
    .ceed-kpi-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,.07); }
    .ceed-kpi-accent {
      position: absolute; top: 0; left: 0; bottom: 0; width: 4px;
      border-radius: 10px 0 0 10px;
    }
    .ceed-kpi-icon {
      width: 36px; height: 36px; border-radius: 8px;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 12px;
    }
    .ceed-kpi-value {
      font-family: 'DM Sans', sans-serif;
      font-size: 2.4rem; font-weight: 700; line-height: 1; color: #0c1118;
      letter-spacing: .02em;
    }
    .ceed-kpi-label {
      font-family: 'DM Sans', sans-serif;
      font-size: 10px; letter-spacing: .06em; text-transform: none;
      color: #9ca3af; margin-top: 4px;
    }
    .ceed-kpi-sub { font-size: 11.5px; color: #6b7280; margin-top: 3px; }

    /* Two-col layout */
    .ceed-two-col {
      display: grid;
      grid-template-columns: 1fr 320px;
      gap: 20px;
    }
    @media (max-width: 1023px) { .ceed-two-col { grid-template-columns: 1fr; } }

    /* Panel cards */
    .ceed-panel {
      background: #fff;
      border: 1px solid #e8ecf0;
      border-radius: 10px;
      overflow: hidden;
      margin-bottom: 20px;
    }
    .ceed-panel:last-child { margin-bottom: 0; }
    .ceed-panel-head {
      display: flex; align-items: center; justify-content: space-between;
      padding: 16px 20px;
      border-bottom: 1px solid #f3f4f6;
    }
    .ceed-panel-title {
      font-size: 14px; font-weight: 600; color: #0c1118;
      display: flex; align-items: center; gap: 8px;
    }
    .ceed-panel-link {
      font-family: 'DM Sans', sans-serif;
      font-size: 11px; letter-spacing: .06em; color: #9ca3af;
      text-decoration: none; display: flex; align-items: center; gap: 4px;
      transition: color 160ms;
    }
    .ceed-panel-link:hover { color: #111111; }
    .ceed-panel-body { padding: 0; }

    /* List rows */
    .ceed-list-row {
      display: flex; align-items: center; justify-content: space-between;
      padding: 14px 20px;
      border-bottom: 1px solid #f9fafb;
      transition: background 150ms;
    }
    .ceed-list-row:last-child { border-bottom: none; }
    .ceed-list-row:hover { background: #fafafa; }
    .ceed-list-row-name { font-size: 13.5px; font-weight: 500; color: #0c1118; }
    .ceed-list-row-sub { font-size: 11.5px; color: #9ca3af; margin-top: 2px; }
    .ceed-list-row-badge {
      font-family: 'DM Sans', sans-serif;
      font-size: 10px; letter-spacing: .06em;
      padding: 3px 9px; border-radius: 5px;
      font-weight: 500; white-space: nowrap;
    }

    /* Status badge colors */
    .badge-pending   { background: rgba(0,0,0,0.06); color: #333333; }
    .badge-processing { background: rgba(59,130,246,.1);  color: #1d4ed8; }
    .badge-completed { background: rgba(16,185,129,.1);   color: #065f46; }
    .badge-open      { background: rgba(99,102,241,.1);   color: #3730a3; }
    .badge-default   { background: #f3f4f6; color: #6b7280; }

    /* Empty / loading states */
    .ceed-empty {
      padding: 40px 20px; text-align: center;
      font-size: 13px; color: #9ca3af;
    }
    .ceed-loading {
      padding: 40px 20px; text-align: center;
      display: flex; align-items: center; justify-content: center; gap: 8px;
      font-size: 13px; color: #9ca3af;
    }

    /* Quick actions */
    .ceed-qa-link {
      display: flex; align-items: center; gap: 12px;
      padding: 13px 20px;
      border-bottom: 1px solid #f3f4f6;
      text-decoration: none;
      transition: background 150ms;
    }
    .ceed-qa-link:last-child { border-bottom: none; }
    .ceed-qa-link:hover { background: #fafafa; }
    .ceed-qa-icon {
      width: 34px; height: 34px; border-radius: 8px;
      background: #f4f5f7;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0; color: #0c1118;
      transition: background 150ms;
    }
    .ceed-qa-link:hover .ceed-qa-icon { background: rgba(0,0,0,0.08); color: #111111; }
    .ceed-qa-text { flex: 1; min-width: 0; }
    .ceed-qa-name { font-size: 13.5px; font-weight: 500; color: #0c1118; }
    .ceed-qa-desc { font-size: 11.5px; color: #9ca3af; margin-top: 1px; }
    .ceed-qa-arrow { color: #d1d5db; flex-shrink: 0; transition: transform 150ms, color 150ms; }
    .ceed-qa-link:hover .ceed-qa-arrow { transform: translateX(3px); color: #000000; }

    /* Notifications */
    .ceed-notif-item {
      padding: 12px 20px;
      border-bottom: 1px solid #f3f4f6;
    }
    .ceed-notif-item:last-child { border-bottom: none; }
    .ceed-notif-title { font-size: 13px; font-weight: 500; color: #0c1118; }
    .ceed-notif-msg { font-size: 12px; color: #6b7280; margin-top: 2px; }
    .ceed-notif-time {
      font-family: 'DM Sans', sans-serif;
      font-size: 10px; color: #d1d5db; margin-top: 4px; letter-spacing: .04em;
    }
  `;
  document.head.appendChild(el);
}

/* ── Helpers ──────────────────────────────────────────────────── */
function statusBadgeClass(status = "") {
  const s = status.toLowerCase();
  if (s.includes("pending")) return "badge-pending";
  if (s.includes("processing")) return "badge-processing";
  if (s.includes("completed") || s.includes("approved")) return "badge-completed";
  if (s.includes("open") || s.includes("submitted")) return "badge-open";
  return "badge-default";
}

function fmtStatus(s = "") {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ── Sub-components ───────────────────────────────────────────── */
function KpiCard({ accentColor, iconBg, icon, value, label, sub, loading }) {
  return (
    <div className="ceed-kpi-card">
      <div className="ceed-kpi-accent" style={{ background: accentColor }} />
      <div className="ceed-kpi-icon" style={{ background: iconBg }}>
        {icon}
      </div>
      <div className="ceed-kpi-value">
        {loading ? (
          <Loader2 size={22} style={{ animation: "spin 1s linear infinite", display: "inline-block" }} />
        ) : (
          value ?? 0
        )}
      </div>
      <div className="ceed-kpi-label">{label}</div>
      {sub && <div className="ceed-kpi-sub">{sub}</div>}
    </div>
  );
}

function PanelCard({ title, titleIcon, linkTo, linkLabel = "View all", children }) {
  return (
    <div className="ceed-panel">
      <div className="ceed-panel-head">
        <div className="ceed-panel-title">
          {titleIcon && <span style={{ color: "#111111" }}>{titleIcon}</span>}
          {title}
        </div>
        {linkTo && (
          <Link to={linkTo} className="ceed-panel-link">
            {linkLabel} <ArrowRight size={12} />
          </Link>
        )}
      </div>
      <div className="ceed-panel-body">{children}</div>
    </div>
  );
}

/* ── Main Dashboard ───────────────────────────────────────────── */
export default function CeedDashboard() {
  const isMobile = useIsMobile();
  const { user } = useAuth();

  const canPurchasePlans = can(user, "ceed.purchaseplans");
  const canQuotes = can(user, "ceed.quotes");
  const canReports = can(user, "ceed.reports");
  const canUsers = can(user, "ceed.users");

  const [loading, setLoading] = useState(true);
  const [ppSum, setPpSum] = useState(null);
  const [qtSum, setQtSum] = useState(null);
  const [recentPP, setRecentPP] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [notif, setNotif] = useState([]);
  const [errFlags, setErrFlags] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => { injectStyles(); }, []);

  async function load() {
    setLoading(true);
    const nextErr = {};
    try {
      if (canPurchasePlans) {
        const [sum, list] = await Promise.all([
          jfetch(`/api/purchase-plans/summary`).catch(() => { nextErr.pp = true; return null; }),
          jfetch(`/api/purchase-plans/recent?limit=5`).catch(() => { nextErr.ppList = true; return { rows: [] }; }),
        ]);
        setPpSum(sum);
        setRecentPP(list?.rows || []);
      }
      if (canQuotes) {
        const [sum, list] = await Promise.all([
          jfetch(`/api/quotes/summary?scope=ceed`).catch(() => { nextErr.q = true; return null; }),
          jfetch(`/api/quotes/recent?limit=5&scope=ceed`).catch(() => { nextErr.qList = true; return { rows: [] }; }),
        ]);
        setQtSum(sum);
        setRecentQuotes(list?.rows || []);
      }
      const n = await jfetch(`/api/notifications/recent?limit=6`).catch(() => ({ rows: [] }));
      setNotif(n?.rows || []);
      setLastUpdated(new Date());
    } finally {
      setErrFlags(nextErr);
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [canPurchasePlans, canQuotes]); // eslint-disable-line

  const ppStats = useMemo(() => ({
    pending: ppSum?.pending ?? 0,
    completedThisWeek: ppSum?.completedThisWeek ?? 0,
    total: ppSum?.total ?? 0,
  }), [ppSum]);

  const quoteStats = useMemo(() => ({
    open: qtSum?.open ?? 0,
    sentThisWeek: qtSum?.sentThisWeek ?? 0,
    total: qtSum?.total ?? 0,
  }), [qtSum]);

  const displayName =
    user?.firstname || user?.lastname
      ? `${user?.firstname || ""} ${user?.lastname || ""}`.trim()
      : user?.email || "Manager";

  return (
    <div className="ceed-db">
      {/* ── Header ── */}
      <div className="ceed-db-header">
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <div className="ceed-db-title">
              Welcome back{displayName !== "Manager" ? `, ${displayName.split(" ")[0]}` : ""}
            </div>
            <div className="ceed-db-sub">
              CEED Business Console &nbsp;·&nbsp;
              {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : "Loading…"}
            </div>
          </div>
          <button className="ceed-db-refresh" onClick={load} disabled={loading} title="Refresh">
            <RefreshCw size={14} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
            {!isMobile && <span>Refresh</span>}
          </button>
        </div>
      </div>

      {/* ── KPI row ── */}
      <div className="ceed-kpi-grid" style={isMobile ? { gridTemplateColumns: "repeat(2,1fr)" } : undefined}>
        <KpiCard
          accentColor="#111111"
          iconBg="rgba(0,0,0,0.06)"
          icon={<DollarSign size={17} color="#333333" />}
          value={ppStats.pending}
          label="Pending Plans"
          sub={canPurchasePlans ? "Awaiting review" : "No access"}
          loading={loading && canPurchasePlans}
        />
        <KpiCard
          accentColor="#10b981"
          iconBg="rgba(16,185,129,.1)"
          icon={<Download size={17} color="#065f46" />}
          value={ppStats.completedThisWeek}
          label="Completed This Week"
          sub={canPurchasePlans ? "Delivered PDFs" : "No access"}
          loading={loading && canPurchasePlans}
        />
        <KpiCard
          accentColor="#6366f1"
          iconBg="rgba(99,102,241,.1)"
          icon={<FileText size={17} color="#3730a3" />}
          value={quoteStats.open}
          label="Open Quotes"
          sub={canQuotes ? "Awaiting response" : "No access"}
          loading={loading && canQuotes}
        />
        <KpiCard
          accentColor="#0c1118"
          iconBg="#f4f5f7"
          icon={<Bell size={17} color="#374151" />}
          value={notif.length}
          label="New Notifications"
          sub="Latest system updates"
          loading={loading}
        />
      </div>

      {/* ── Two-column body ── */}
      <div className="ceed-two-col">
        {/* Left column */}
        <div>
          {/* Recent Purchase Plans */}
          {canPurchasePlans && (
            <PanelCard
              title="Recent Purchase Plans"
              titleIcon={<HiCurrencyDollar size={16} />}
              linkTo="/ceed/purchase-plans"
            >
              {loading ? (
                <div className="ceed-loading">
                  <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
                  Loading…
                </div>
              ) : errFlags.ppList ? (
                <div className="ceed-empty">
                  Failed to load.{" "}
                  <Link to="/ceed/purchase-plans" style={{ color: "#111111" }}>
                    Go to Purchase Plans →
                  </Link>
                </div>
              ) : recentPP.length === 0 ? (
                <div className="ceed-empty">No recent purchase plans.</div>
              ) : (
                recentPP.map((p) => (
                  <div className="ceed-list-row" key={p.id}>
                    <div>
                      <div className="ceed-list-row-name">
                        {p.projectName || p.project?.name || "Project"}
                      </div>
                      <div className="ceed-list-row-sub">
                        {new Date(p.createdAt).toLocaleString()}
                      </div>
                    </div>
                    <span className={`ceed-list-row-badge ${statusBadgeClass(p.status)}`}>
                      {fmtStatus(p.status || "open")}
                    </span>
                  </div>
                ))
              )}
            </PanelCard>
          )}

          {/* Recent Quotes */}
          {canQuotes && (
            <PanelCard
              title="Recent Quotes"
              titleIcon={<FileText size={15} />}
              linkTo="/ceed/quotes"
            >
              {loading ? (
                <div className="ceed-loading">
                  <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
                  Loading…
                </div>
              ) : errFlags.qList ? (
                <div className="ceed-empty">
                  Failed to load.{" "}
                  <Link to="/ceed/quotes" style={{ color: "#111111" }}>
                    Go to Quotes →
                  </Link>
                </div>
              ) : recentQuotes.length === 0 ? (
                <div className="ceed-empty">No recent quotes.</div>
              ) : (
                recentQuotes.map((q) => (
                  <div className="ceed-list-row" key={q.id}>
                    <div>
                      <div className="ceed-list-row-name">
                        {q.projectName || q.project?.name || q.projectId}
                      </div>
                      <div className="ceed-list-row-sub">
                        Updated {new Date(q.updatedAt).toLocaleString()}
                      </div>
                    </div>
                    <span className={`ceed-list-row-badge ${statusBadgeClass(q.status)}`}>
                      {fmtStatus(q.status || "submitted")}
                    </span>
                  </div>
                ))
              )}
            </PanelCard>
          )}

          {/* If no perm panels show */}
          {!canPurchasePlans && !canQuotes && (
            <div className="ceed-panel" style={{ padding: "40px 20px", textAlign: "center" }}>
              <ShieldCheck size={32} style={{ color: "#d1d5db", margin: "0 auto 12px" }} />
              <div style={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>
                No panels available
              </div>
              <div style={{ fontSize: "13px", color: "#9ca3af", marginTop: "4px" }}>
                Contact your platform admin to enable permissions.
              </div>
            </div>
          )}
        </div>

        {/* Right column */}
        <div>
          {/* Quick Actions */}
          <PanelCard title="Quick Actions" titleIcon={<Activity size={15} />}>
            {canPurchasePlans && (
              <Link to="/ceed/purchase-plans" className="ceed-qa-link">
                <div className="ceed-qa-icon"><DollarSign size={16} /></div>
                <div className="ceed-qa-text">
                  <div className="ceed-qa-name">Purchase Plans</div>
                  <div className="ceed-qa-desc">Set price & process PDFs</div>
                </div>
                <ArrowRight size={15} className="ceed-qa-arrow" />
              </Link>
            )}
            {canQuotes && (
              <Link to="/ceed/quotes" className="ceed-qa-link">
                <div className="ceed-qa-icon"><FileText size={16} /></div>
                <div className="ceed-qa-text">
                  <div className="ceed-qa-name">Quotes Oversight</div>
                  <div className="ceed-qa-desc">Monitor & route quotes</div>
                </div>
                <ArrowRight size={15} className="ceed-qa-arrow" />
              </Link>
            )}
            {canReports && (
              <Link to="/ceed/reports" className="ceed-qa-link">
                <div className="ceed-qa-icon"><TrendingUp size={16} /></div>
                <div className="ceed-qa-text">
                  <div className="ceed-qa-name">Reports</div>
                  <div className="ceed-qa-desc">Export KPIs & summaries</div>
                </div>
                <ArrowRight size={15} className="ceed-qa-arrow" />
              </Link>
            )}
            {canUsers && (
              <Link to="/ceed/users" className="ceed-qa-link">
                <div className="ceed-qa-icon"><Users2 size={16} /></div>
                <div className="ceed-qa-text">
                  <div className="ceed-qa-name">Users (read-only)</div>
                  <div className="ceed-qa-desc">Browse user accounts</div>
                </div>
                <ArrowRight size={15} className="ceed-qa-arrow" />
              </Link>
            )}
            <Link to="/ceed/profile" className="ceed-qa-link">
              <div className="ceed-qa-icon"><ShieldCheck size={16} /></div>
              <div className="ceed-qa-text">
                <div className="ceed-qa-name">My Profile</div>
                <div className="ceed-qa-desc">Update account details</div>
              </div>
              <ArrowRight size={15} className="ceed-qa-arrow" />
            </Link>
          </PanelCard>

          {/* Notifications */}
          <PanelCard
            title="Notifications"
            titleIcon={<Bell size={15} />}
            linkTo="/ceed/notifications"
            linkLabel="All notifications"
          >
            {loading ? (
              <div className="ceed-loading">
                <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
                Loading…
              </div>
            ) : notif.length === 0 ? (
              <div className="ceed-empty">You're all caught up.</div>
            ) : (
              notif.map((n) => (
                <div className="ceed-notif-item" key={n.id}>
                  <div className="ceed-notif-title">
                    {n.title || n.type?.replaceAll("_", " ") || "Update"}
                  </div>
                  {n.message && (
                    <div className="ceed-notif-msg">{n.message}</div>
                  )}
                  <div className="ceed-notif-time">
                    {new Date(n.createdAt).toLocaleString()}
                  </div>
                </div>
              ))
            )}
            <div style={{ padding: "12px 20px", borderTop: "1px solid #f3f4f6" }}>
              <Link
                to="/ceed/notifications"
                style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#9ca3af", textDecoration: "none" }}
              >
                <Mail size={13} /> Open Notification Center
              </Link>
            </div>
          </PanelCard>
        </div>
      </div>

      {/* Keyframe for spinner */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

/* ── Icon import for purchase plans ─────────────────────────── */
function HiCurrencyDollar({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}
